import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Box, Button, Flex, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {CoverStep} from '@amblique/king-living/app/components/complex-customize/cover-step';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {
  OptionSlotProductPicker,
  SubtotalFooter,
} from '@amblique/king-living/app/components/complex-customize/option-step';
import {getCustomizeStepSlotKind} from '@amblique/king-living/app/hooks/use-customize-steps';

/**
 * Renders the product picker sub-view for a single step slot (cover or option).
 * Shown when the user taps "Choose" on a slot card.
 */
export const ProductPickerView = ({
  activeSlot,
  bodyHeight,
  coverGroups,
  currency,
  footerProduct,
  highlightedOptionProductId,
  isMobile,
  isSimplePDP = false,
  masterMinPrice,
  onSelect,
  onSlotProductPreview,
  onSlotNext,
  onViewDetails,
  scrollBodyRef,
  selections,
  selectionSummary,
  showSubtotalFooter,
  stepData,
  styles,
  subtotalListPrice,
  subtotalPrice,
}) => {
  const intl = useIntl();
  const showListPrice = subtotalListPrice > 0 && subtotalListPrice > (subtotalPrice ?? 0);

  const subtotalPriceText = subtotalPrice != null && subtotalPrice > 0 ? (
    <Flex alignItems="baseline" gap={1} wrap="wrap">
      <FormattedMessage
        defaultMessage="Package price {price}"
        id="customize_panel.footer.subtotal"
        values={{
          price: formatCurrencyHideTrailingZeroCents(intl, subtotalPrice, currency),
        }}
      />
      {showListPrice && (
        <Text as="span" color="greyscale.70" fontSize="xs" textDecoration="line-through">
          {formatCurrencyHideTrailingZeroCents(intl, subtotalListPrice, currency)}
        </Text>
      )}
    </Flex>
  ) : null;

  // Simple PDP labels the picker confirm CTA as "Done" (per FSD §SECTION-PDP-SIMPLE-08
  // design — Configurable Master flow has no follow-up step after a slot is confirmed,
  // so the action wording reflects completion rather than progression). Complex PDP keeps
  // "Next" since the flyout cascades through subsequent steps after each picker confirm.
  // Multi-slot shelves: joined title can list prior slots while Next gates on the open slot.
  const canConfirmNext = footerProduct?.canConfirmSlot ?? Boolean(footerProduct?.name);
  const NextButton = (
    <>
      <Button
        {...styles.subtotalDoneButton}
        aria-describedby={!canConfirmNext ? 'customize-next-hint' : undefined}
        aria-disabled={!canConfirmNext}
        id="customize-next-btn"
        isDisabled={!canConfirmNext}
        tabIndex={canConfirmNext ? 0 : -1}
        type="button"
        onClick={onSlotNext}
      >
        {isSimplePDP ? (
          <FormattedMessage defaultMessage="Done" id="customize_panel.done_button" />
        ) : (
          <FormattedMessage defaultMessage="Next" id="customize_panel.footer.next" />
        )}
      </Button>
      {!canConfirmNext && (
        <Text className="sr-only" id="customize-next-hint">
          <FormattedMessage
            defaultMessage="Select an option to continue"
            id="customize_panel.footer.next_hint"
          />
        </Text>
      )}
    </>
  );

  if (activeSlot.stepId === 'cover') {
    return (
      <>
        <Box
          ref={scrollBodyRef}
          className="customize-step-cover-content-body"
          height={bodyHeight}
          overflowY="auto"
          paddingX={{base: 4, lg: 6}}
          paddingY={4}
          sx={{WebkitOverflowScrolling: 'touch', touchAction: 'pan-y'}}
          {...styles.body}
        >
          <CoverStep
            coverGroups={coverGroups}
            currency={currency}
            isMobile={isMobile}
            masterMinPrice={masterMinPrice}
            selection={selections}
            onSelect={onSelect}
            onViewDetails={onViewDetails}
          />
        </Box>
        {showSubtotalFooter && (
          <SubtotalFooter
            emptyTitleContext="nestedSelection"
            footerProduct={footerProduct}
            priceText={subtotalPriceText}
            selectionSummary={selectionSummary}
            styles={styles}
          >
            {NextButton}
          </SubtotalFooter>
        )}
      </>
    );
  }

  // Option slot product picker
  const value = stepData?.container?.values?.[activeSlot.slotIndex];
  const slotSelections = selections?.[activeSlot.stepId] || [];
  const optionPickerStepKind = stepData ? getCustomizeStepSlotKind(stepData) : 'other';
  const pickerBodyWrapperClassName = [
    'configuration-panel-body-wrapper',
    `configuration-panel-body-wrapper--customize-step--${optionPickerStepKind}`
  ].join(' ');
  const containerDescription = stepData?.container?.containerDescription?.trim();

  return (
    <Box
      className={pickerBodyWrapperClassName}
      display="flex"
      flex={1}
      flexDirection="column"
      minHeight={0}
      overflow="hidden"
    >
      <Box
        ref={scrollBodyRef}
        className="customize-steps-content-body configuration-selector-panel-content__body"
        flex={bodyHeight ? undefined : 1}
        height={bodyHeight}
        minH={bodyHeight ? undefined : 0}
        overflowY="auto"
        sx={{WebkitOverflowScrolling: 'touch', touchAction: 'pan-y'}}
        {...styles.body}
      >
        {containerDescription && (
          <Text className="customize-picker-container-description" {...styles.pickerContainerDescription}>
            {containerDescription}
          </Text>
        )}
        {value && (
          <OptionSlotProductPicker
            container={stepData.container}
            customizeStepKind={optionPickerStepKind}
            highlightedProductId={highlightedOptionProductId}
            isSimplePDP={isSimplePDP}
            selections={slotSelections}
            slotIndex={activeSlot.slotIndex}
            styles={styles}
            value={value}
            onSlotProductPreview={onSlotProductPreview}
            onViewDetails={onViewDetails}
          />
        )}
      </Box>
      {showSubtotalFooter && (
        <SubtotalFooter
          emptyTitleContext="nestedSelection"
          footerProduct={footerProduct}
          priceText={subtotalPriceText}
          selectionSummary={selectionSummary}
          styles={styles}
        >
          {NextButton}
        </SubtotalFooter>
      )}
    </Box>
  );
};

ProductPickerView.displayName = 'ProductPickerView';

ProductPickerView.propTypes = {
  activeSlot: PropTypes.shape({stepId: PropTypes.string, slotIndex: PropTypes.number}).isRequired,
  bodyHeight: PropTypes.number,
  coverGroups: PropTypes.array.isRequired,
  currency: PropTypes.string,
  footerProduct: PropTypes.object,
  highlightedOptionProductId: PropTypes.string,
  isMobile: PropTypes.bool,
  isSimplePDP: PropTypes.bool,
  masterMinPrice: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onSlotProductPreview: PropTypes.func,
  onSlotNext: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
  scrollBodyRef: PropTypes.object.isRequired,
  selections: PropTypes.object,
  selectionSummary: PropTypes.string,
  showSubtotalFooter: PropTypes.bool,
  stepData: PropTypes.object,
  styles: PropTypes.object.isRequired,
  subtotalListPrice: PropTypes.number,
  subtotalPrice: PropTypes.number,
};
